<!--
	Tabs: an ARIA tablist with arrow/Home/End navigation and one panel for the active tab.
	A disabled active tab falls back to the last enabled one.
-->
<script lang="ts" generics="T">
	import type { Snippet } from 'svelte';
	import { tablistKeys } from '#lib/tablist.js';

	interface Props {
		items: T[];
		/** Selected index (bindable). */
		active?: number;
		labelFor: (item: T) => string;
		isDisabled?: (item: T, index: number) => boolean;
		/** Accessible name for the tablist. */
		label: string;
		panel: Snippet<[T, number]>;
	}

	let {
		items,
		active = $bindable(0),
		labelFor,
		isDisabled = () => false,
		label,
		panel
	}: Props = $props();

	const id = $props.id();

	const disabled = $derived(items.map((item, i) => isDisabled(item, i)));
	const shown = $derived(disabled[active] ? disabled.lastIndexOf(false) : active);
</script>

<div class="tabList" role="tablist" aria-label={label} {@attach tablistKeys}>
	{#each items as item, i (i)}
		<button
			type="button"
			role="tab"
			id="{id}-tab-{i}"
			aria-controls="{id}-panel"
			aria-selected={shown === i}
			aria-disabled={disabled[i]}
			tabindex={shown === i ? 0 : -1}
			class="tab"
			onclick={() => {
				if (!disabled[i]) active = i;
			}}>
			{labelFor(item)}
		</button>
	{/each}
</div>

{#if shown >= 0}
	<div role="tabpanel" id="{id}-panel" aria-labelledby="{id}-tab-{shown}" class="panel">
		{@render panel(items[shown], shown)}
	</div>
{/if}

<style>
	.tabList {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-6);
		border-bottom: 1px solid var(--border-color);
	}

	/* The border is always present (transparent until selected) so selection doesn't shift the row. */
	.tab {
		cursor: pointer;
		margin-bottom: -1px;
		border: 0;
		border-bottom: 3px solid transparent;
		background: none;
		padding: var(--space-2) var(--space-1);
		min-height: var(--target-min);
		color: var(--text-muted);
		font-weight: var(--weight-bold);

		&:hover:not([aria-disabled='true']) {
			color: var(--text-color);
		}

		&[aria-selected='true'] {
			border-bottom-color: var(--accent-text);
			color: var(--text-color);
		}

		&[aria-disabled='true'] {
			cursor: not-allowed;
			color: var(--disabled-text);
		}

		&:focus-visible {
			outline-offset: -2px;
		}
	}
</style>
